import React, {useState} from 'react';
import {internalApi} from "../../common/axios";
import {buildUrl} from "../../shared/utils/net";
import toast from "react-hot-toast";
import {DIALOG_KEY, IS_DOCKER} from "../../shared/utils/enum";
import {Card, Input, Button, Select, Typography, Space, DatePicker, Popconfirm} from 'antd';
import {DeleteOutlined} from '@ant-design/icons';
import {CancelButton, DeleteButton, SaveButton} from "../../shared/components/buttons/CustomButtons";
import {NoticeEntity} from "./NoticeEntity";
import dayjs from "dayjs";
import Editor from "../../shared/components/editor/Editor";
import {useCountryData} from "../country/useCountryData";
import IsLoading from "../../shared/components/IsLoading";
import ErrorPage from "../../shared/components/ErrorPage";

const NoticeEditModal = ({record, handleClose, dialogRef, handleSetDialog}) => {
    const data = new NoticeEntity(record);
    const [rows, setRows] = useState(data);
    const [notices, setNotices] = useState(data.notice || {});
    const {data: countryData, isLoading, error} = useCountryData();

    if (isLoading) return <IsLoading/>;
    if (error) return <ErrorPage error={error}/>;

    const langCodes = [...new Set(countryData.map(row => row.langCode))];

    const cloudFrontUrl = IS_DOCKER
        ? `${process.env.REACT_APP_IMAGE_URL}/repo/`
        : process.env.REACT_APP_CLOUDFRONT_URL;

    const handleSetRows = (key, value) => {
        setRows((prevState) => {
            const updatedData = {...prevState, [key]: value};
            return new NoticeEntity(updatedData);
        });

        if (key === 'target' && value === 0) {
            handleAddBanner('US');
        }
    }

    const handleDelete = async (e) => {
        e.preventDefault();
        const currentDialog = dialogRef.current;  // ref에서 최신 상태 가져오기
        if (currentDialog.confirmText === 'DELETE') {
            await internalApi.delete(buildUrl('banner/notice', {seq: rows.seq, data: rows.toJson()})).then((res) => {
                toast.success(res.data.message);
                handleClose(true);
            }).catch((err) => {
                toast.error("삭제 실패");
                console.error(err);
            });

            handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ""}]);
        } else {
            toast.error("잘못된 확인 문자입니다. DELETE 를 입력하세요.");
            handleSetDialog([{key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
        }
    }

    const handleSave = async (e) => {
        e.preventDefault();

        if (rows.isInvalidate()) return;

        await internalApi.post('banner/notice', {seq: rows.seq, data: rows.toJson()}).then((res) => {
            toast.success(res.data.message);
            handleClose(true);
        }).catch((err) => {
            console.error(err);
            toast.error("저장 실패");
        });

        handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ""}]);
    }

    const handleSetNotice = (countryCode, key, value) => {
        setNotices((prevNotice) => {
            const newNotice = {...prevNotice};
            newNotice[countryCode][key] = value;
            handleSetRows('notice', newNotice);
            return newNotice;
        });
    }

    const handleSetNoticeBody = (countryCode, value) => {
        setNotices((prevNotice) => {
            const newNotice = {...prevNotice};
            newNotice[countryCode].body = value;
            handleSetRows('notice', newNotice);
            return newNotice;
        });
    }

    const getAddresses = (row, key) => {
        return `${cloudFrontUrl}notice/${row.fileName}.html?lang=${key}`;
    }

    const handleAddBanner = (countryCode) => {
        if (notices[countryCode]) {
            return;
        }

        setNotices((prevNotice) => {
            const newNotice = {...prevNotice};
            newNotice[countryCode] = {title: '', body: '', date: ''};
            handleSetRows('notice', newNotice);
            return newNotice;
        });
    }

    const handleRemoveNotice = (countryCode) => {
        setNotices((prevNotice) => {
            const newNotice = {...prevNotice};
            delete newNotice[countryCode];
            handleSetRows('notice', newNotice);
            return newNotice;
        });
    };
    return (
        <div className={"bannerCreate"} style={{marginTop: 26}}>
            <div>
                <Typography.Title level={5} className={"title"}>주소 형식:
                    https://production.repos.kf.actgames.co/notice/[파일이름].html?lang=[언어]</Typography.Title>
                <Typography.Text>(에디터를 통해 자동으로 HTML 태그가 적용됩니다.)</Typography.Text>
            </div>
            <Space style={{display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: 8}}>
                <Input
                    value={rows.fileName}
                    placeholder="파일 이름을 입력하세요(영문, 중복 금지)"
                    style={{width: 300, margin: 5}}
                    onChange={e => handleSetRows('fileName', e.target.value)}
                />
                <DatePicker
                    value={rows.noticeDate ? dayjs(rows.noticeDate) : null}
                    format="YYYY-MM-DD"
                    onChange={date => handleSetRows('noticeDate', date ? dayjs(date) : null)}
                    style={{margin: 5}}
                    placeholder="공지 일"
                />
                <Input
                    value={rows.memo}
                    placeholder="Memo"
                    style={{width: 250, marginLeft: 5, marginTop: 5, marginBottom: 5}}
                    onChange={e => handleSetRows('memo', e.target.value)}
                />
            </Space>
            {Object.keys(rows.notice).map((key, index) => (
                <div key={index} style={{margin: '5px'}}>
                    <Card size="small">
                        <Input
                            placeholder={`${key} 타이틀`}
                            value={rows.notice[key].title}
                            style={{width: '99%', margin: '5px'}}
                            onChange={e => handleSetNotice(key, `title`, e.target.value)}
                        />
                        <Space style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            margin: '5px',
                            width: '100%'
                        }}>
                            <Input
                                value={getAddresses(rows, key)}
                                style={{width: '94%'}}
                                readOnly
                                placeholder="주소"
                            />
                            <Popconfirm title="정말 삭제하시겠습니까?" onConfirm={() => handleRemoveNotice(key)} okText="삭제"
                                        cancelText="취소">
                                <Button danger icon={<DeleteOutlined/>} size="small" style={{marginLeft: 8}}/>
                            </Popconfirm>
                        </Space>
                        <div>
                            <Editor
                                setData={(data) => handleSetNoticeBody(key, data)}
                                data={rows.notice[key].body}
                                uploadUrl={'upload/notice/images'}
                            />
                        </div>
                    </Card>
                </div>
            ))}
            <div className={"param"} style={{margin: 5}}>
                <Select
                    placeholder="공지 추가"
                    value={undefined}
                    style={{width: 200}}
                    onChange={handleAddBanner}
                >
                    {langCodes.map((el) => (
                        <Select.Option key={el} value={el} disabled={notices[el]}>
                            {el}
                        </Select.Option>
                    ))}
                </Select>
            </div>
            <div className={"buttons"}>
                <SaveButton onClick={handleSave} handleSetDialog={handleSetDialog}/>
                <CancelButton onClick={handleClose} handleSetDialog={handleSetDialog}/>
                <DeleteButton onClick={handleDelete} display={rows.seq === 0 ? 'none' : 'flex'}
                              handleSetDialog={handleSetDialog}/>
            </div>
        </div>
    );
}

export default NoticeEditModal;